import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactBoxes from '../components/ContactBoxes';
import { COMPANY_INFO, SERVICES_LIST } from '../data/mockData';

export default function ServicesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [cmsGallery, setCmsGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Find matching service from fallback list or CMS
    const foundService = SERVICES_LIST.find(s => s.slug === slug);
    if (!foundService && slug) {
      // If invalid service slug, navigate to default service
      navigate('/ydelser/service-og-ventilation', { replace: true });
      return;
    }
    setService(foundService);

    // Fetch related gallery items from CMS
    fetch(`https://api.dev.auxo.dk/public/v2/content/galleri`)
      .then(res => res.json())
      .then(data => {
        if (data && data.items) {
          const matching = data.items.filter(item => item.tilknyttet_ydelse === slug || item.tilknyttet_ydelse === foundService?.slug);
          setCmsGallery(matching);
        }
      })
      .catch(err => console.error('CMS fetch gallery error:', err))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (!service) return null;

  // Custom verbatim content blocks matching source scrape per service
  const renderServiceContent = () => {
    switch (service.slug) {
      case 'service-og-ventilation':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900">Vi udfører service af ventilation</h2>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                Hos K-TO Multiservice tilbyder vi service af ventilationsanlæg og mindre køleanlæg. Vi udfører alle slags services; det kan bl.a. være et filterskifte. Vores arbejde udføres altid professionelt, kompetent og effektivt.
              </p>
              <p>
                Vi har stor erfaring med vedligeholdelse og indregulering af ventilationsanlæg, og vi kan være behjælpelige med den helt rigtige løsning. Vi tilpasser altid løsningen efter præcis de behov, du må have.
              </p>
              <p>
                Har du brandautomatik på dit ventilationsanlæg, er det også vigtigt at få lavet et serviceeftersyn af denne. Det er et lovkrav at få lavet et tjek af brandautomatikken én gang om året. Alt dette kan vi hos K-TO Multiservice nemt hjælpe dig med.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-sky-50 border border-sky-200/80 space-y-3">
              <h3 className="font-bold text-lg text-sky-950">Årligt servicetjek giver lavere energiudgifter & bedre indeklima</h3>
              <p className="text-xs text-sky-900 leading-relaxed">
                Vores ekspert anbefaling er, at du får udført et årligt servicetjek af dit ventilationsanlæg for at sikre et driftsikkert anlæg, lavere energiudgifter og et bedre indeklima. Uden hyppig service af dit ventilationsanlæg, kan der ske fejl, som kan resultere i høje udgifter til reparation samt et energitab.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-semibold text-sky-950 pt-2">
                <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600"></i> Filterskift & rensning</li>
                <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600"></i> Måling af luftmængder</li>
                <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600"></i> Tjek af brandautomatik</li>
                <li className="flex items-center gap-2"><i className="fa-solid fa-check text-sky-600"></i> Test af komponenter & styring</li>
              </ul>
            </div>
          </div>
        );

      case 'indregulering':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900">Indregulering og lydregulering af ventilation</h2>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                Hos K-TO Multiservice tilbyder vi indregulering af ventilation. En indregulering foretages, når et ventilationsanlæg skal sættes i drift, og på den måde bliver der sikret de korrekte luftmængder i rummet med mindst mulig støj, trækgener og strømforbrug. Når indreguleringen er færdig, laver vi en målerapport, som bliver tilsendt til dig.
              </p>
              <p>
                Det er altid vigtigt, at et ventilationsanlæg bliver indreguleret, så der er balance mellem udsugning og indblæsning. Det er et lovkrav at få gjort dette, hvilket K-TO Multiservice nemt kan ordne for dig. Vi sørger for korrekt opstart af ventilationsanlæg og fortrådning mellem alle de eksterne komponenter inden for 15 meter.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <span className="block font-bold text-slate-900 text-sm mb-1">Korrekte luftmængder</span>
                <p className="text-xs text-slate-600">Præcis balancering af indblæsning og udsugning i alle lokaler.</p>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <span className="block font-bold text-slate-900 text-sm mb-1">Lyd- og støjregulering</span>
                <p className="text-xs text-slate-600">Reduktion af støjgener og uønsket træk for et behageligt arbejdsmiljø.</p>
              </div>
            </div>
          </div>
        );

      case 'fejlfinding-og-reparation':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900">Fejlfinding og reparation af ventilationsanlæg</h2>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                Hos K-TO Multiservice tilbyder vi fejlfinding og reparation af køleanlæg og ventilationsanlæg. Det kan være meget forskelligt, hvad der skal laves og repareres; det kan være, at noget er gået i stykker, f.eks. en motor eller en føler, hvis en rem skal skiftes, eller hvis anlægget er indreguleret forkert eller slet ikke indreguleret. Der kan forekomme mange forskellige slags problemer, som vi selvfølgelig løser professionelt og kompetent for dig.
              </p>
              <p>
                Vi har stor erfaring med service og indregulering af ventilationsanlæg, og vi kan være behjælpelige med den helt rigtige løsning. Vi tilpasser altid løsningen efter præcis de behov, du må have.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-amber-50 border border-amber-200 space-y-3">
              <h3 className="font-bold text-base text-amber-950 flex items-center gap-2">
                <i className="fa-solid fa-triangle-exclamation text-amber-600"></i>
                <span>Oplever I støj, alarmer eller manglende luftmængde?</span>
              </h3>
              <p className="text-xs text-amber-900">
                Ring direkte på 22 72 87 23 for hurtig fejlfinding og kompetent udbedring af defekte motorer, remme, følere eller styringsfejl.
              </p>
            </div>
          </div>
        );

      case 'salg-og-montering':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900">Salg og montering af ventilationsanlæg</h2>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                Hos K-TO Ventilationsteknik tilbyder vi faglig rådgivning, salg og montering af ventilationsanlæg, kanaler og komponenter til erhverv, industri, kontorer og lagerbygninger.
              </p>
              <p>
                Vi varetager hele forløbet fra rådgivning om den rette anlægsstørrelse til korrekt fysisk montering, el-tilslutning og opstart. Efter monteringen indregulerer vi anlægget grundigt og udarbejder dokumenteret målerapport.
              </p>
            </div>
          </div>
        );

      case 'rensning-af-ventilation':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900">Rensning af ventilationsanlæg og ventilationskanaler</h2>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                Hos K-TO Multiservice tilbyder vi rensning og rengøring af alle slags ventilationskanaler og -anlæg. Et ventilationsanlæg skal gerne blive ved med at levere frisk luft i mange år, men hvis anlægget ikke bliver renset og vedligeholdt, fungerer anlægget ikke optimalt og kan f.eks. i værste fald være med til, at din bygning får skimmelsvamp samt fremkaldelse af allergiske reaktioner og andre gener hos personer i bygningen. Vi sørger for at fjerne støv, fibre, bakterier m.m. fra dit anlæg, så bygningen forbliver sund at opholde sig i.
              </p>
              <p>
                Vi har stor erfaring med service og indregulering af ventilationsanlæg, og vi kan være behjælpelige med den helt rigtige løsning. Vi tilpasser altid løsningen efter præcis de behov, du må have.
              </p>
            </div>
          </div>
        );

      case 'kol':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900">Mindre køleanlæg og køl i ventilation</h2>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                K-TO Multiservice monterer og servicerer mindre køleanlæg professionelt og kompetent. Vi har stor erfaring med køleanlæg, især køl i ventilationsanlæg, og vi kan være behjælpelige med den helt rigtige løsning. Vi tilpasser altid løsningen efter præcis de behov, du må have.
              </p>
              <p>
                Vi hjælper med at opsætte og indregulere køleanlæg, og vi tilbyder også fejlfinding, service og reparation af køleanlæg. Vi hjælper med at tjekke dit anlæg og sørger for energioptimering, så du holder omkostningerne på et fornuftigt niveau og får det optimale ud af dit køleanlæg.
              </p>
            </div>
          </div>
        );

      case 'varmepumper':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-extrabold text-slate-900">Varmepumper til erhverv og industri</h2>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                Vi tilbyder installation og service af luft-til-luft varmepumper, som både kan levere køling og varme. En varmepumpe er ofte en langt mere økonomisk løsning, både i indkøb og drift, sammenlignet med traditionelle varmesystemer.
              </p>
              <p>
                Vores servicetekniker sørger for korrekt dimensionering, montering, fejlfinding og årligt servicetjek for at sikre maksimal effektivitet og lang levetid.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-16 pb-16">
      <SEO 
        title={service.metaTitle || `${service.name} - K-TO Ventilationsteknik`}
        description={service.metaDescription || service.shortDesc}
        slug={`/ydelser/${service.slug}`}
        image={service.ogImage || service.image}
        exact={Boolean(service.metaTitle)}
      />

      {/* Header */}
      <section className="bg-slate-900 text-white py-16 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-sky-500/20 text-sky-300 text-xs font-bold uppercase tracking-wider border border-sky-500/30">
            <i className={service.icon} aria-hidden="true"></i>
            <span>Ydelser</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
            {service.name}
          </h1>

          <p className="text-slate-300 text-base sm:text-lg max-w-3xl leading-relaxed">
            {service.shortDesc}
          </p>
        </div>
      </section>

      {/* Main Body */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-sm space-y-6">
              {renderServiceContent()}

              <div className="pt-6 border-t border-slate-200 flex flex-wrap items-center gap-4">
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-2 nowrap-contact"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring på 22 72 87 23</span>
                </a>
                <Link
                  to="/kontakt"
                  className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors inline-flex items-center gap-2"
                >
                  <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                  <span>Få et uforpligtende tilbud</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 border-b border-slate-100 pb-3">
                Alle Vores Ydelser
              </h3>
              <ul className="space-y-1">
                {SERVICES_LIST.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/ydelser/${item.slug}`}
                      className={`block px-3 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between ${
                        item.slug === service.slug
                          ? 'bg-sky-50 text-sky-700 font-extrabold'
                          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <i className={`${item.icon} text-sky-600 text-sm`} aria-hidden="true"></i>
                        <span>{item.name}</span>
                      </span>
                      <i className="fa-solid fa-chevron-right text-[10px] opacity-40" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-900 text-white rounded-3xl p-6 border border-slate-800 space-y-4">
              <span className="text-xs font-bold text-sky-400 uppercase tracking-wider block">
                Brug for rådgivning?
              </span>
              <h3 className="text-lg font-black text-white">Vi kører i hele Jylland og på Fyn</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Kontakt K-TO Ventilationsteknik i dag. Vi tilbyder gennemskuelige tilbud på service, indregulering og reparation.
              </p>
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-wider py-3 rounded-xl border border-slate-700 transition-colors flex items-center justify-center gap-2 block text-center"
              >
                <i className="fa-solid fa-envelope text-sky-400" aria-hidden="true"></i>
                <span>Send e-mail</span>
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Contact Boxes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ContactBoxes />
      </section>
    </div>
  );
}
